import { useEffect, useState } from "react";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { telHref, waHref, PHONE, LOGO_IMAGE } from "@/constants/site";
import useCallTracking from "@/hooks/useCallTracking";
import useEventTracking from "@/hooks/useEventTracking";
import { useT } from "@/i18n/LanguageContext";
import TopControls from "@/components/TopControls";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useT();
  const trackEvent = useEventTracking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const trackCall = useCallTracking();

  const links = [
    { id: "services", label: t.nav.services, testid: NAV.linkServices },
    { id: "areas", label: t.nav.areas, testid: NAV.linkAreas },
    { id: "faq", label: t.nav.faq, testid: NAV.linkFaq },
    { id: "book", label: t.nav.book, testid: "nav-link-book" },
    { id: "contact", label: t.nav.contact, testid: NAV.linkContact },
  ];

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "header-glass" : "bg-transparent"
      }`}
      style={{ top: "var(--policy-bar-h)" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 lg:h-24 flex items-center justify-between gap-3">
        {/* Logo */}
        <a
          href="#top"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group shrink-0"
          aria-label="TyresPro Ltd home"
        >
          <img
            src={LOGO_IMAGE}
            alt="TyresPro Ltd"
            className="h-12 sm:h-16 lg:h-[68px] w-auto object-contain transition-transform group-hover:scale-105"
            loading="eager"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={l.testid}
              href={`#${l.id}`}
              className="text-sm font-medium text-[#0A0A0A] dark:text-white hover:text-[#1E63FF] dark:hover:text-[#1E63FF] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right cluster: language + theme + call CTA */}
        <div className="flex items-center gap-2">
          <TopControls />

          {/* Desktop call + WhatsApp */}
          <div className="hidden md:flex items-center gap-2 ml-1">
            <a
              data-testid={NAV.ctaWhatsapp}
              href={waHref()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackEvent("whatsapp", "header")}
              className="btn-outline-black px-4 h-10 rounded-full inline-flex items-center gap-2 text-sm font-semibold"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2} />
              {t.nav.whatsapp}
            </a>
            <a
              data-testid={NAV.ctaCall}
              href={telHref}
              onClick={trackCall("header")}
              className="btn-blue px-5 h-10 rounded-full inline-flex items-center gap-2 text-sm font-semibold pulse-blue"
            >
              <Phone className="w-4 h-4" strokeWidth={2.2} />
              {PHONE}
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid={NAV.mobileToggle}
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden md:hidden w-10 h-10 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden header-glass border-t border-black/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[#0A0A0A] dark:text-white"
              >
                {l.label}
              </a>
            ))}
            <div className="flex gap-2 pt-3">
              <a
                href={waHref()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-black flex-1 h-11 rounded-full inline-flex items-center justify-center gap-2 text-sm font-semibold"
              >
                <MessageCircle className="w-4 h-4" /> {t.nav.whatsapp}
              </a>
              <a
                href={telHref}
                onClick={trackCall("header-mobile")}
                className="btn-blue flex-1 h-11 rounded-full inline-flex items-center justify-center gap-2 text-sm font-semibold"
              >
                <Phone className="w-4 h-4" /> {t.nav.call}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
